import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/client';
import { completeWhatsAppLogin, startWhatsAppLogin, type WhatsAppLink } from '../api/endpoints/auth';
import type { LoginResponse } from '../api/schemas';

/**
 * "Entrar con WhatsApp" on the web. The person proves their number by SENDING
 * Dale a message that carries a one-time code (from WhatsApp on the phone, or
 * WhatsApp Web on this computer), and this page polls until the server has
 * seen it. Same rules as the apps:
 * - The poll token is the only thing that collects the session. It lives in a
 *   ref for one attempt: never logged, never stored.
 * - It asks only while the tab is visible, and right away when it comes back.
 * - A number the server proved but no access opens yet comes back as a link:
 *   the next password login carries it and links it.
 */

const MIN_POLL_MS = 1_000;
const MAX_POLL_MS = 10_000;
const RATE_LIMITED_WAIT_MS = 10_000;
/** A bad connection is mentioned only after it has missed this many polls in a row. */
const NETWORK_TROUBLE_AFTER = 3;

export type WhatsAppLoginPhase =
  /** Nothing in flight. */
  | 'idle'
  /** Asking the server for a code. */
  | 'starting'
  /** The message is on screen; polling until Dale has it. */
  | 'waiting'
  /** The session arrived and the page is moving on. */
  | 'signingIn'
  /** Proven, but no access opens with this number yet: the password links it. */
  | 'notLinked'
  /** The ten minutes ran out. */
  | 'expired';

export type WhatsAppLoginMessage = {
  /** The whole message, ready to send as is. */
  text: string;
  /** The one-time code inside it, shown on its own so it can be typed by hand. */
  code: string;
  whatsappUrl: string;
};

export type WhatsAppLogin = {
  phase: WhatsAppLoginPhase;
  message?: WhatsAppLoginMessage;
  /** Set in `notLinked`: what the next password login sends to link the number. */
  link?: WhatsAppLink;
  /** A sentence for the person, cleared on the next action. */
  error?: string;
  start: () => Promise<void>;
  cancel: () => void;
  /** The password login used the link (or the person gave up on it). */
  forgetLink: () => void;
};

type Attempt = WhatsAppLink & { expiresAtMs: number; pollEveryMs: number };

type Options = {
  /** The session the poll collected; the page stores it and moves on. */
  onSignedIn: (result: LoginResponse) => void;
  deviceId: () => string;
};

/** The code is the last word of the message (`... Código: ABCD2345`). */
export function codeIn(text: string): string {
  const match = /([A-Z0-9]{8})\s*$/.exec(text.trim());
  return match?.[1] ?? '';
}

function sentenceFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'Demasiados intentos. Espera un momento.';
    if (error.status === 503) return 'Entrar con WhatsApp no está disponible ahora. Entra con tu usuario.';
    return error.message;
  }
  return 'No pudimos completar eso ahora mismo.';
}

export function useWhatsAppLogin({ onSignedIn, deviceId }: Options): WhatsAppLogin {
  const [phase, setPhase] = useState<WhatsAppLoginPhase>('idle');
  const [message, setMessage] = useState<WhatsAppLoginMessage | undefined>();
  const [link, setLink] = useState<WhatsAppLink | undefined>();
  const [error, setError] = useState<string | undefined>();

  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;
  const deviceIdRef = useRef(deviceId);
  deviceIdRef.current = deviceId;

  /** Bumped to retire an attempt: an answer that belongs to an older one is dropped. */
  const generation = useRef(0);
  const attempt = useRef<Attempt | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const asking = useRef(false);
  const misses = useRef(0);

  const clearTimer = useCallback(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const retire = useCallback(() => {
    generation.current += 1;
    attempt.current = undefined;
    clearTimer();
  }, [clearTimer]);

  // `poll` and `schedule` call each other, so `poll` lives behind a ref.
  const pollRef = useRef<(id: number) => Promise<void>>(async () => undefined);

  const schedule = useCallback(
    (delayMs: number, id: number) => {
      clearTimer();
      timer.current = setTimeout(() => void pollRef.current(id), delayMs);
    },
    [clearTimer],
  );

  pollRef.current = async (id: number) => {
    const current = attempt.current;
    if (id !== generation.current || !current || asking.current) return;
    // Hidden tab: the visibility listener asks again the moment it is back.
    if (document.visibilityState === 'hidden') return;
    if (Date.now() >= current.expiresAtMs) {
      retire();
      setPhase('expired');
      setError('Se venció el código. Pide uno nuevo.');
      return;
    }
    asking.current = true;
    const next = (delayMs: number) =>
      schedule(Math.max(0, Math.min(delayMs, current.expiresAtMs - Date.now())), id);
    try {
      const result = await completeWhatsAppLogin({
        challengeId: current.challengeId,
        pollToken: current.pollToken,
        deviceId: deviceIdRef.current(),
      });
      if (id !== generation.current) return;
      misses.current = 0;
      if ('status' in result) {
        setError(undefined);
        next(current.pollEveryMs);
        return;
      }
      retire();
      setPhase('signingIn');
      onSignedInRef.current(result);
    } catch (caught) {
      if (id !== generation.current) return;
      if (caught instanceof ApiError && caught.code === 'whatsapp_not_linked') {
        const proven = { challengeId: current.challengeId, pollToken: current.pollToken };
        retire();
        setLink(proven);
        setPhase('notLinked');
        setError(undefined);
      } else if (caught instanceof ApiError && caught.status === 401) {
        retire();
        setPhase('expired');
        setError('Se venció el código. Pide uno nuevo.');
      } else if (caught instanceof ApiError && caught.status === 429) {
        next(RATE_LIMITED_WAIT_MS);
      } else if (!(caught instanceof ApiError)) {
        misses.current += 1;
        if (misses.current >= NETWORK_TROUBLE_AFTER) {
          setError('Sin conexión por ahora. Seguimos esperando tu mensaje.');
        }
        next(current.pollEveryMs);
      } else {
        // Asking again will not fix this one.
        retire();
        setPhase('idle');
        setError(sentenceFor(caught));
      }
    } finally {
      asking.current = false;
    }
  };

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'hidden' || !attempt.current) return;
      clearTimer();
      void pollRef.current(generation.current);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      generation.current += 1;
      attempt.current = undefined;
      clearTimer();
    };
  }, [clearTimer]);

  const start = useCallback(async () => {
    retire();
    const id = generation.current;
    misses.current = 0;
    setPhase('starting');
    setError(undefined);
    setLink(undefined);
    try {
      const challenge = await startWhatsAppLogin();
      if (id !== generation.current) return;
      const pollEveryMs = Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, challenge.pollAfterSec * 1_000));
      attempt.current = {
        challengeId: challenge.challengeId,
        pollToken: challenge.pollToken,
        expiresAtMs: Date.now() + challenge.expiresInSec * 1_000,
        pollEveryMs,
      };
      setMessage({ text: challenge.text, code: codeIn(challenge.text), whatsappUrl: challenge.whatsappUrl });
      setPhase('waiting');
      schedule(pollEveryMs, id);
    } catch (caught) {
      if (id !== generation.current) return;
      setPhase('idle');
      setError(sentenceFor(caught));
    }
  }, [retire, schedule]);

  const cancel = useCallback(() => {
    retire();
    setPhase('idle');
    setMessage(undefined);
    setError(undefined);
  }, [retire]);

  const forgetLink = useCallback(() => {
    setLink(undefined);
    setPhase((current) => (current === 'notLinked' ? 'idle' : current));
  }, []);

  return { phase, message, link, error, start, cancel, forgetLink };
}
